import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthModal({ isOpen, onClose, onSuccess }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });

  // Forgot password state
  const [view, setView] = useState('tabs'); // 'tabs' | 'forgot' | 'forgot-sent'
  const [forgotEmail, setForgotEmail] = useState('');

  const resetToTabs = () => {
    setView('tabs');
    setForgotEmail('');
  };

  const handleClose = (open) => {
    if (!open) {
      resetToTabs();
      onClose();
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, loginData);
      localStorage.setItem('token', response.data.token);
      toast.success('Logged in successfully!');
      onSuccess(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, registerData);
      localStorage.setItem('token', response.data.token);
      toast.success('Account created successfully!');
      onSuccess(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setView('forgot-sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass-heavy border-white/10 text-white max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {view === 'forgot' || view === 'forgot-sent' ? 'Reset password' : 'Welcome'}
          </DialogTitle>
        </DialogHeader>

        {view === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-5 mt-4" data-testid="forgot-password-form">
            <p className="text-sm text-white/70 leading-relaxed">
              Enter the email associated with your account and we&rsquo;ll send you a link to set a new password.
            </p>
            <div>
              <Label htmlFor="forgot-email" className="text-white/80">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                required
                data-testid="forgot-email-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
              disabled={isLoading}
              data-testid="forgot-submit-btn"
            >
              {isLoading ? 'Sending...' : 'Send reset link'}
            </Button>
            <button
              type="button"
              onClick={resetToTabs}
              className="w-full text-xs uppercase tracking-[0.2em] text-white/60 hover:text-gold transition-colors"
              data-testid="forgot-back-btn"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        {view === 'forgot-sent' && (
          <div className="space-y-5 mt-4 text-center" data-testid="forgot-sent-confirmation">
            <div className="mx-auto w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
              <svg className="w-6 h-6 text-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">
              If an account exists for <span className="text-gold">{forgotEmail}</span>, we&rsquo;ve sent a password reset link.
              Please check your inbox &mdash; the link expires in 60 minutes.
            </p>
            <Button
              type="button"
              onClick={resetToTabs}
              className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]"
              data-testid="forgot-sent-done-btn"
            >
              Done
            </Button>
          </div>
        )}

        {view === 'tabs' && (
        <Tabs defaultValue="login" className="mt-4">
          <TabsList className="grid w-full grid-cols-2 bg-white/5">
            <TabsTrigger value="login" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A192F]" data-testid="login-tab">
              Sign In
            </TabsTrigger>
            <TabsTrigger value="register" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A192F]" data-testid="register-tab">
              Sign Up
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4 mt-6">
            <Button
              onClick={handleGoogleLogin}
              className="w-full bg-white text-[#0A192F] hover:bg-white/90"
              data-testid="google-login-btn"
            >
              Continue with Google
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#0A192F] px-2 text-white/60">Or</span>
              </div>
            </div>

            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-white/80">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password" className="text-white/80">Password</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(loginData.email);
                      setView('forgot');
                    }}
                    className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-gold transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot?
                  </button>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]" disabled={isLoading} data-testid="email-login-btn">
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register" className="space-y-4 mt-6">
            <Button
              onClick={handleGoogleLogin}
              className="w-full bg-white text-[#0A192F] hover:bg-white/90"
              data-testid="google-register-btn"
            >
              Continue with Google
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#0A192F] px-2 text-white/60">Or</span>
              </div>
            </div>

            <form onSubmit={handleEmailRegister} className="space-y-4">
              <div>
                <Label htmlFor="register-name" className="text-white/80">Name</Label>
                <Input
                  id="register-name"
                  type="text"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label htmlFor="register-email" className="text-white/80">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="register-password" className="text-white/80">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className="bg-transparent border-b border-white/20 rounded-none px-0 focus:border-gold"
                  required
                  data-testid="register-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D]" disabled={isLoading} data-testid="email-register-btn">
                {isLoading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
